// Deterministic fill of the DG "GC Evaluation" sheet from survey answers.
//
// Each line item's Basis/Notes (column C) is annotated with the survey context
// that justifies it, and GC OVERRIDE (column D) gets the cost computed from
// the Formula Reference Guide's rates where the survey provides the inputs.
// Confirmation and timing columns (E–I) stay manual — the sheet's own
// Estimated Total formulas only count a row once the GC confirms it, so
// filling D is a suggestion, not an activation.
//
// Rows are located by their column-B service label (not hardcoded row
// numbers), so template edits that shift rows don't misfire; HVAC cost cells
// are live formulas in the template and are deliberately never written.

import type { CellRestyle, CellWrite } from "./xlsxTemplatePatcher.ts";
import { readRowStyles, readSheetColumnFormulas, readSheetColumnText } from "./xlsxTemplatePatcher.ts";

export const GC_EVALUATION_SHEET = "GC Evaluation";

/**
 * Strips the [section] suffix from a question title and lowercases it —
 * same normalization as useTemplateOptions.cleanTitle, duplicated here so
 * this module stays free of the supabase/firebase import chain (testable).
 */
function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
}

/**
 * Dash/whitespace-insensitive title key: survey templates vary between
 * em-dashes, hyphens, and double spaces ("Unit 1 — Tonnage" vs
 * "Unit 1 - Tonnage"), and a strict lookup misfires the rules. Unit
 * questions are further canonicalized: surveys name them "HVAC 4 - Age"
 * while the workbook says "Unit 4 — Age (years)" — both become
 * "unit 4-age".
 */
export function normalizeTitleKey(title: string): string {
  return cleanTitle(title)
    .replace(/[—–-]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/\s*-\s*/g, "-")
    .replace(/^hvac (\d+)\b/, "unit $1")
    .replace(/^(unit \d+-age) \(years\)$/, "$1");
}

/** "N/A" in any spelling (NA, n/a, N.A., n-a…) — treated as no answer at export. */
export function isNotApplicable(value: unknown): boolean {
  return typeof value === "string" && /^n[\s./\\-]*a\.?$/i.test(value.trim());
}

/** Answer helpers over the export's answer map (see buildAnswerMap). */
export class SurveyAnswers {
  private byNormalizedTitle = new Map<string, unknown>();
  private byNormalizedTitleRaw = new Map<string, unknown>();

  constructor(map: Map<string, unknown>) {
    for (const [key, value] of map) {
      if (!key.startsWith("title:")) continue;
      const normalized = normalizeTitleKey(key.slice("title:".length));
      if (!this.byNormalizedTitleRaw.has(normalized)) this.byNormalizedTitleRaw.set(normalized, value);
      // Rule logic (notes, costs, gates) reads N/A as unanswered; cell writes
      // use raw() and always stamp N/A as-is (the answer_status behind it is
      // the gate, not the workbook's format column).
      if (isNotApplicable(value)) continue;
      if (!this.byNormalizedTitle.has(normalized)) this.byNormalizedTitle.set(normalized, value);
    }
  }

  /** The stored answer value, untouched — N/A included (cell-write path). */
  raw(title: string): unknown {
    return this.byNormalizedTitleRaw.get(normalizeTitleKey(title));
  }

  text(title: string): string | null {
    const raw = this.byNormalizedTitle.get(normalizeTitleKey(title));
    if (raw === null || raw === undefined) return null;
    const text = Array.isArray(raw) ? raw.join(", ") : String(raw).trim();
    return text === "" ? null : text;
  }

  /** First numeric token of the answer ("22,450 SF (Estimated…)" → 22450). */
  num(title: string): number | null {
    const text = this.text(title);
    const m = text?.match(/-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?/);
    if (!m) return null;
    const n = Number(m[0].replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  yes(title: string): boolean | null {
    const text = this.text(title);
    if (!text) return null;
    if (/^yes\b/i.test(text)) return true;
    if (/^no\b/i.test(text)) return false;
    return null;
  }

  /**
   * Same as yes(), trying each candidate title in order and returning the
   * first that resolves. A question's wording can change across catalog/
   * template revisions (confirmed: the electrical panel protection question
   * exists as "McCue Panel Cover installed at electrical panels?" on some
   * properties and "Is there a safety railing (in front of/infront of) the
   * electrical panels?" on others, same leaf_id) — a rule keyed to only one
   * phrasing silently reads as unanswered on every property still using
   * (or only ever having used) another.
   */
  yesAny(titles: string[]): boolean | null {
    for (const title of titles) {
      const v = this.yes(title);
      if (v !== null) return v;
    }
    return null;
  }
}

const fmt = (n: number) => n.toLocaleString("en-US");

const CONDITION_LABELS: Record<number, string> = {
  0: "poor",
  1: "poor",
  2: "fair",
  3: "moderate",
  4: "good",
  5: "excellent",
};
const cond = (n: number | null) =>
  n === null ? null : `${n}${CONDITION_LABELS[n] ? ` (${CONDITION_LABELS[n]})` : ""}`;

/** "Both Missing" → 2, "1 Missing" → 1, "Both RR Correct"/"All Functional" → 0. */
function restroomCount(raw: string | null): number | null {
  if (!raw) return null;
  if (/both.*(missing|broken|incorrect)/i.test(raw)) return 2;
  const n = raw.match(/\d+/);
  if (n) return Number(n[0]);
  if (/correct|functional|yes|good|none/i.test(raw)) return 0;
  return null;
}

interface RuleResult {
  note?: string | null;
  cost?: number | null;
}

type Rule = (a: SurveyAnswers) => RuleResult;

/** Meaningful free-text answer, or null for blanks/N-A/None. */
function comment(raw: string | null): string | null {
  if (!raw || /^(n\/?a|none|no)\.?$/i.test(raw.trim())) return null;
  return raw.length > 90 ? `${raw.slice(0, 89)}…` : raw;
}

function hvacUnitRule(unit: number): Rule {
  return (a) => {
    const tons = a.num(`Unit ${unit} — Tonnage`);
    const age = a.num(`Unit ${unit} — Age (years)`) ?? a.num(`Unit ${unit} — Age`);
    const action = a.text(`Unit ${unit} — Action`);
    if (tons === null && action === null) {
      // Only assert the unit's absence when other units DID resolve —
      // otherwise a title mismatch would stamp wrong notes over every row.
      const anyUnit = [1, 2, 3, 4, 5, 6].some(
        (u) => a.num(`Unit ${u} — Tonnage`) !== null || a.text(`Unit ${u} — Action`) !== null
      );
      return anyUnit ? { note: `No Unit ${unit} per survey` } : {};
    }
    const parts: string[] = [];
    let cost: number | undefined;
    if (tons !== null && action) {
      const rate = action === "Replace" ? 3500 : 350;
      cost = Math.floor(tons * rate);
      parts.push(`${tons}t × $${fmt(rate)}/ton (${action}) = $${fmt(cost)}`);
    } else if (action) {
      // Cost is tonnage × rate; without tons the row can't price and will be
      // cleared/greyed — name the missing input so the gap is actionable.
      parts.push(`${action} — tonnage MISSING per survey (cost pending)`);
    }
    if (age !== null) parts.push(`${age} yrs old`);
    // A surveyed age of 0 was dropped upstream (dropUnusableUnitAges) rather
    // than printed as an age. Say the age is missing instead of leaving the
    // basis silent — a blank there reads as "no age question", not "the age
    // we hold is unusable", and it explains why the ≥10 yrs rule stayed out.
    else {
      const gap = a.text(`Unit ${unit} — Age Gap Note`);
      if (gap) parts.push(gap);
    }
    // The export corrects Tune-Up→Replace for units at/over the age
    // threshold; say so, since it moves the line from $350 to $3,500/ton.
    // The disclosure leads and the outcome ends the sentence — the cell clips
    // from the left, so a tail of `…recorded "Tune-Up")` read as if the
    // evaluation itself said Tune-Up.
    const override = a.text(`Unit ${unit} — Action Override Note`);
    if (override) parts.push(`${override} — auto-set to Replace (≥10 yrs rule)`);
    return { note: parts.length ? parts.join("  |  ") : null, cost };
  };
}

/**
 * Rules keyed by the template's column-B service label. `note` replaces the
 * Basis/Notes cell; `cost` fills GC OVERRIDE. Omitted/null values leave the
 * template cell untouched.
 */
const RULES: Record<string, Rule> = {
  // ── HVAC (older templates price these with live formulas — the computed
  // cost is only stamped when the template's D cell has none) ──
  "HVAC — Unit 1": hvacUnitRule(1),
  "HVAC — Unit 2": hvacUnitRule(2),
  "HVAC — Unit 3": hvacUnitRule(3),
  "HVAC — Unit 4": hvacUnitRule(4),
  "HVAC — Unit 5": hvacUnitRule(5),
  "HVAC — Unit 6": hvacUnitRule(6),
  "HVAC — Add Tonnage (Zone minimum)": (a) => {
    const deficit = a.num("Tonnage deficit / add");
    const required = a.num("Required tonnage per zone (Building SF ÷ Zone SF/Ton, round up to 0.5t)");
    const existing = a.num("Existing total tonnage (sum of all units)");
    const zone = a.text("Climate Zone");
    if (deficit === null) return {};
    if (deficit <= 0) {
      const met = [
        existing !== null ? `existing ${existing}T` : null,
        required !== null && zone ? `meets Zone ${zone} min (${required}T)` : "meets zone minimum",
      ].filter(Boolean);
      return { note: `No deficit — ${met.join(" ")}` };
    }
    const detail = [
      `${deficit}t × $3,500/ton = $${fmt(Math.floor(deficit * 3500))}`,
      required !== null && zone ? `Zone ${zone} min: ${required}T` : null,
      existing !== null ? `existing: ${existing}T` : null,
    ].filter(Boolean);
    return { note: detail.join("  |  "), cost: Math.floor(deficit * 3500) };
  },
  "HVAC — Ductwork Repair": (a) => {
    const c = comment(a.text("Additional HVAC comments"));
    return { note: c ? `Optional — re. "${c}" (per manager)` : null };
  },
  "HVAC — Backroom Unit Repair": (a) => {
    const n = a.num("Number of HVAC / Heater units in backroom");
    const condition = a.text("Backroom HVAC or Heater unit condition / noise feedback");
    if (n === null) return {};
    return {
      note: `Optional — ${n} backroom unit${n === 1 ? "" : "s"}${condition ? `, ${condition.toLowerCase()} condition` : ""} per surveyor`,
    };
  },
  "HVAC — Mini-Split (cooler / back office)": (a) => {
    const type = a.text("Mini-split diffuser type");
    if (!type) return {};
    return {
      note: /no minisplit/i.test(type)
        ? "Optional — no minisplit per survey"
        : `Optional — minisplit type: ${type}`,
    };
  },

  // ── ELECTRICAL ──
  "McCue Panel Cover": (a) => {
    // Same question, three confirmed template wordings — see yesAny().
    const installed = a.yesAny([
      "McCue Panel Cover installed at electrical panels?",
      "Is there a safety railing in front of the electrical panels?",
      "Is there a safety railing infront of the electrical panels?",
    ]);
    if (installed === null) return {};
    return {
      note: installed
        ? "McCue panel cover already installed per survey"
        : "$700 fixed  |  Install in front of electrical panel — cover missing per survey",
      cost: installed ? null : 700,
    };
  },
  "Electrical Panel Upgrade": (a) => {
    const amps = a.num("Current amperage to building (total to Dollar General)");
    const phasing = a.text("Current electrical service phasing");
    const store = a.text("Store Type (Based off of FY27)");
    if (amps === null || !phasing || !store) return {};
    const threePhase = /3/.test(phasing);
    // DGS / DGH: 400A 3-phase OR 600A 1-phase; every other format: 600A 3-phase.
    const relaxed = /^(DGS|DGH)$/i.test(store);
    const compliant = relaxed ? (threePhase ? amps >= 400 : amps >= 600) : threePhase && amps >= 600;
    return {
      note: `$25,000  |  Current ${amps}A ${threePhase ? "3-phase" : "1-phase"} — ${compliant ? "compliant" : "upgrade required"} for ${store.toUpperCase()}`,
      cost: compliant ? null : 25000,
    };
  },
  "Switch / Outlet Replacement": (a) => {
    const raw = a.text("Number of broken or damaged switches and outlets (office + breakroom)");
    if (!raw) return {};
    const n = a.num("Number of broken or damaged switches and outlets (office + breakroom)");
    return {
      note: `Optional — $75/each  |  ${raw} broken switches/outlets per survey`,
      cost: n !== null && n > 0 ? n * 75 : null,
    };
  },

  // ── PARKING LOT ──
  "Crack Fill + Seal Coat": (a) => {
    const lot = a.num("Parking Lot SF");
    if (lot === null) return {};
    const dmg = a.num("% of parking lot with damaged pavement");
    const depth = a.num("Average depth of damaged areas / holes (inches)");
    const detail = [
      `${fmt(lot)} sf × $0.625/sf`,
      dmg !== null ? `${dmg}% damaged pavement${depth !== null ? `, ${depth}" depth` : ""}` : null,
    ].filter(Boolean);
    return { note: detail.join("  |  "), cost: Math.floor(lot * 0.625) };
  },
  "Parking Lot Restripe": (a) => {
    const lot = a.num("Parking Lot SF");
    if (lot === null) return {};
    const stripe = a.num("Which photo best represents existing parking lot striping quality?");
    return {
      note: `${fmt(lot)} sf × $0.20/sf${stripe !== null ? `  |  striping rating: ${cond(stripe)}` : ""}`,
      cost: Math.floor(lot * 0.2),
    };
  },
  "Bollards — ADA (handicap signage)": (a) => {
    const missing = a.yes("Missing handicap signage?");
    if (missing === null) return {};
    return {
      note: missing
        ? "Optional — handicap signage missing per survey  |  $1,500/ea ADA bollard"
        : "Optional — ADA bollards/signage already present per survey",
    };
  },
  "Wheel Stops / Parking Stops Replacement": (a) => {
    const n = a.num("How many parking stops are missing?");
    if (n === null) return {};
    return {
      note: `Optional — ${n} missing stops per survey${n > 0 ? `  |  ${n} × $150/each` : ""}`,
      cost: n > 0 ? n * 150 : null,
    };
  },
  "Sidewalk Repair (cracks / missing pieces)": (a) => {
    const needed = a.yes("Are sidewalk repair needed? (Large cracks or missing pieces)");
    if (needed === null) return {};
    return {
      note: needed
        ? "Optional — surveyor: sidewalk repair needed"
        : "Optional — surveyor: no sidewalk repair needed",
    };
  },
  "Sidewalk — Seal and Refill": () => ({
    note: "Optional — assume ~300 sf × $4.00/sf = $1,200",
  }),
  "Parking Lot Resurfacing — Good Scope": (a) => {
    const r = a.num("Which photo best matches existing parking lot condition?");
    return r === null
      ? {}
      : { note: `Optional — for lot rating 4+  |  survey rating: ${cond(r)}` };
  },
  "Parking Lot Resurfacing — Better Scope": (a) => {
    const r = a.num("Which photo best matches existing parking lot condition?");
    return r === null
      ? {}
      : { note: `Optional — for lot rating 2–3  |  survey rating: ${cond(r)}` };
  },
  "Parking Lot Resurfacing — Best Scope": (a) => {
    const r = a.num("Which photo best matches existing parking lot condition?");
    return r === null
      ? {}
      : { note: `Optional — for lot rating 0–1  |  survey rating: ${cond(r)}` };
  },
  "Storm Drain Cleaning": (a) => {
    const issues = a.yes("Evidence of drainage issues?");
    if (issues === null) return {};
    return {
      note: issues
        ? "Optional — drainage issues observed per survey"
        : "Optional — no drainage issues per survey",
    };
  },

  // ── INTERIOR ──
  "Sales Floor Overlay": (a) => {
    const sales = a.num("Sales Floor SF");
    if (sales === null) return {};
    const c = a.num("Sales floor condition");
    return {
      note: `${fmt(sales)} sf × $5.00/sf  |  LVT/VCT${c !== null ? `  |  Floor cond: ${cond(c)}` : ""}`,
      cost: Math.floor(sales * 5),
    };
  },
  "Receiving Room — Paint, Reseal & Restripe": (a) => {
    const recv = a.num("Receiving Room SF");
    if (recv === null) return {};
    const c = a.num("Receiving room floor condition");
    return {
      note: `$1.25/sf × ${fmt(recv)} sf${c !== null ? `  |  Floor cond: ${cond(c)}` : ""}`,
      cost: Math.floor(recv * 1.25),
    };
  },
  "Full Interior Repaint": (a) => {
    const bldg = a.num("Total Building SF");
    if (bldg === null) return {};
    const wall = a.num("Wall / paint condition");
    return {
      note: `${fmt(bldg)} sf × $1.25/sf${wall !== null ? `  |  Wall paint: ${cond(wall)}` : ""}`,
      cost: Math.floor(bldg * 1.25),
    };
  },
  "Ceiling Grill Clean + Paint": (a) => {
    const grills = a.num("Ceiling grills count (for clean & paint)");
    if (grills === null) return {};
    return { note: `${grills} × $35/grill (per survey)`, cost: grills * 35 };
  },
  "Interior Door Paint": () => ({
    note: "8 doors × $75/door (assumed)",
    cost: 600,
  }),
  "Ceiling Tile Replacement — Spot Repair": (a) => {
    // The tile-count question repeats per area with near-identical titles; sum
    // whatever counts resolved (title collisions collapse to the first).
    const counts = [
      a.num("Number of broken, damaged, missing or stained ceiling tiles"),
      a.num("Number of broken, damaged, missing, or stained ceiling tiles"),
    ].filter((n): n is number => n !== null);
    if (counts.length === 0) return {};
    const total = counts.reduce((s, n) => s + n, 0);
    // Reference guide "Ceiling Tile Repair Only": damaged SF × $1.80/sf. The
    // survey captures tile counts, not SF — convert at 8 sf per 2×4 tile.
    // ($25/tile is the guide's office/breakroom rate, not this row's basis.)
    const sf = total * 8;
    return {
      note: `${total} tiles ≈ ${fmt(sf)} sf × $1.80/sf = $${fmt(Math.floor(sf * 1.8))} (per survey counts)`,
      cost: total > 0 ? Math.floor(sf * 1.8) : null,
    };
  },
  "New Interior Receiving Doors (Double Stockroom)": (a) => {
    const broken = a.yes("Door / frame to receiving room broken?");
    if (broken === null) return {};
    return broken
      ? { note: "$800 fixed  |  door/frame broken per survey", cost: 800 }
      : { note: "Optional — $800 fixed  |  door not noted as broken per survey" };
  },
  "Ceiling Grid Repair": (a) => {
    const lf = a.text("Approximate linear footage of ceiling grid broken, damaged, or rusted");
    return { note: lf ? `Optional — ${lf} damaged grid per survey` : null };
  },
  "Interior Lighting / LED Retrofit": (a) => {
    const c = comment(a.text("Number of light fixtures with discolored bulbs or outages on sales floor"));
    return { note: c ? `Optional — "${c}" per manager` : null };
  },
  "Emergency / Exit Light Replacement": (a) => {
    const raw = a.text("Number of emergency / exit lights not functioning");
    if (!raw) return {};
    const n = a.num("Number of emergency / exit lights not functioning");
    return {
      note: `Optional — ${raw} not functioning per survey`,
      cost: n !== null && n > 0 ? n * 325 : null,
    };
  },
  "Egress Striping (Receiving)": (a) => {
    const visible = a.yes("Is egress striping visible?");
    if (visible === null) return {};
    return {
      note: visible
        ? "Optional — egress striping visible per surveyor"
        : "Optional — surveyor: egress striping not visible",
    };
  },
  "Cooler / Freezer Service": (a) => {
    const c = comment(a.text("Cooler lineup damage feedback"));
    return { note: c ? `Optional — "${c}" (per manager)` : null };
  },
  "Mini-Split Diffuser Replacement": (a) => {
    const type = a.text("Mini-split diffuser type");
    if (type && /no minisplit/i.test(type)) return { note: "Optional — no minisplits per survey" };
    const n = a.num("Number of broken, rusty, or missing diffusers");
    if (n === null) return {};
    return {
      note: `Optional — ${n} broken diffusers per survey`,
      cost: n > 0 ? n * 250 : null,
    };
  },
  "Office / Breakroom Door Replacement": (a) => {
    const broken = a.yes("Door / frame to office or breakroom broken?");
    if (broken === null) return {};
    return broken
      ? { note: "Door/frame broken per survey  |  $750/door", cost: 750 }
      : { note: "Optional — door not broken per survey" };
  },
  "Office / Breakroom Light Fixture": (a) => {
    const raw = a.text("Number of light fixtures with discolored bulbs or outages (office + breakroom)");
    if (!raw) return {};
    const n = a.num("Number of light fixtures with discolored bulbs or outages (office + breakroom)");
    return {
      note: `Optional — ${raw} per survey`,
      cost: n !== null && n > 0 ? n * 250 : null,
    };
  },

  // ── RESTROOMS ──
  "Restroom #1 — Floor Overlay": (a) => {
    const count = a.num("Number of restrooms in this location");
    if (count === null || count < 1) return {};
    const c = a.num("Restroom floor condition");
    return {
      note: `$2,500 per restroom${c !== null ? `  |  Floor cond: ${cond(c)}` : ""}`,
      cost: 2500,
    };
  },
  "Restroom #2 — Floor Overlay": (a) => {
    const count = a.num("Number of restrooms in this location");
    if (count === null) return {};
    if (count < 2) return { note: `Only ${count} restroom per survey` };
    const c = a.num("Restroom floor condition");
    return {
      note: `$2,500 per restroom${c !== null ? `  |  Floor cond: ${cond(c)}` : ""}`,
      cost: 2500,
    };
  },
  "Soap Dispenser Replacement": (a) => {
    const raw = a.text("Soap dispenser mounted correctly?");
    const n = restroomCount(raw);
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $100  |  "${raw}" per survey`, cost: n * 100 }
      : { note: `Optional — "${raw}" per survey` };
  },
  "Toilet Paper Holder Replacement": (a) => {
    const raw = a.text("Toilet paper holders needing replacement");
    const n = restroomCount(raw);
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $100  |  "${raw}" per survey`, cost: n * 100 }
      : { note: `Optional — "${raw}" per survey` };
  },
  "Paper Towel Holder Replacement": (a) => {
    const raw = a.text("Paper towel holders needing replacement");
    const n = restroomCount(raw);
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $150  |  "${raw}" per survey`, cost: n * 150 }
      : { note: `Optional — "${raw}" per survey` };
  },
  "Grab Bar Set Replacement": (a) => {
    const raw = a.text("Grab bars installed correctly?");
    const n = restroomCount(raw);
    if (n === null) return {};
    return n > 0
      ? { note: `${n} restroom${n === 1 ? "" : "s"} × $500 (full set)  |  "${raw}" per survey`, cost: n * 500 }
      : { note: `Optional — "${raw}" per survey` };
  },
  "Toilet Replacement": (a) => {
    const n = a.num("Number of broken / damaged toilets");
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $750/toilet  |  per survey`, cost: n * 750 }
      : { note: "Optional — 0 broken toilets per survey" };
  },
  "Sink Replacement": (a) => {
    const n = a.num("Number of broken / damaged sinks");
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $600/sink  |  per survey`, cost: n * 600 }
      : { note: "Optional — 0 broken sinks per survey" };
  },
  "Mirror Replacement": (a) => {
    const n = a.num("Number of broken / damaged mirrors");
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $300/mirror  |  per survey`, cost: n * 300 }
      : { note: "Optional — 0 broken mirrors per survey" };
  },
  "Restroom Door / Frame Replacement": (a) => {
    const n = a.num("Number of broken doors / frames to restrooms");
    if (n === null) return {};
    return n > 0
      ? { note: `${n} × $750/door  |  per survey`, cost: n * 750 }
      : { note: "Optional — 0 broken doors per survey" };
  },
  "Mop Sink Replacement": (a) => {
    const raw = a.text("Mop sink faucet condition");
    if (!raw) return {};
    return /functional|good/i.test(raw)
      ? { note: "Optional — mop sink functional per survey" }
      : { note: `Mop sink: ${raw} per survey  |  $1,500 fixed`, cost: 1500 };
  },
  "Water Fountain Replacement": (a) => {
    const ok = a.yes("Water fountain in good working order (verify plugged in)?");
    if (ok === null) return {};
    return ok
      ? { note: "Optional — fountain in good working order per survey" }
      : { note: "Fountain not working per survey  |  $2,500 fixed", cost: 2500 };
  },
  "Restroom Wall Finish — FRP": (a) => {
    const finish = a.text("Restroom wall finish");
    return { note: finish ? `Optional — $6/sf  |  current wall finish: ${finish.toLowerCase()}` : null };
  },
  "Restroom Wall Finish — Tile": (a) => {
    const finish = a.text("Restroom wall finish");
    return { note: finish ? `Optional — $14/sf  |  current wall finish: ${finish.toLowerCase()}` : null };
  },
  "Restroom Exhaust Fan Replacement": (a) => {
    const raw = a.text("Restroom exhaust fans operability");
    if (!raw) return {};
    return /both functional|all functional/i.test(raw)
      ? { note: "Optional — both exhaust fans functional per survey" }
      : { note: `Exhaust fans: ${raw} per survey  |  $750/fan` };
  },
  "Water Heater Replacement": (a) => {
    const raw = a.text("Water Heater Age?");
    return { note: raw ? `Optional — water heater age: ${raw} per survey  |  $750 installed` : null };
  },
  "Plumbing — Septic Issue Evaluation": (a) => {
    const issues = a.yes("Plumbing / septic issues observed?");
    if (issues === null) return {};
    return issues
      ? { note: "$8,000 fixed  |  plumbing/septic issue observed per survey", cost: 8000 }
      : { note: "Optional — no plumbing/septic issues per survey" };
  },

  // ── PLUMBING ──
  "Sprinkler System Inspection": (a) => {
    const raw = a.text("Latest date of sprinkler inspection (blank if none)");
    return { note: raw ? `Optional — sprinkler inspection date: ${raw}` : null };
  },

  // ── BUILDING EXTERIOR ──
  "Exterior Door Tuneup": (a) => {
    const front = a.text("Front entry door condition");
    const rear = a.text("Receiving / rear door condition");
    if (!front && !rear) return {};
    return {
      note: `Optional — front: ${front ?? "?"}, rear: ${rear ?? "?"} per survey  |  $1,000 tune-up`,
    };
  },
  "Power Wash — Storefront and Sidewalk": (a) => {
    const n = a.num("How many sides of the store need to get powerwashed?");
    if (n === null) return {};
    return { note: `Optional — ${n} sides need powerwashing per survey` };
  },
  "Exterior Paint — Canopy Refresh": (a) => {
    const needed = a.yes("Canopy paint refresh needed?");
    if (needed === null) return {};
    return {
      note: needed
        ? "Canopy paint refresh needed per survey"
        : "Optional — canopy paint refresh: No per survey",
    };
  },
  "Miscellaneous Exterior — Bannerbolts": (a) => {
    const needed = a.yes("Bannerbolts or miscellaneous exterior items?");
    if (needed === null) return {};
    return needed
      ? { note: "$500 fixed  |  bannerbolts/misc items per survey", cost: 500 }
      : { note: "Optional — bannerbolts: No per survey" };
  },
  "New Receiving Doors": (a) => {
    const rear = a.text("Receiving / rear door condition");
    return { note: rear ? `Optional — receiving door condition: ${rear} per survey` : null };
  },
  "Storefront Window Replacement": (a) => {
    const broken = a.yes("Storefront windows broken, chipped, or damaged?");
    if (broken === null) return {};
    return {
      note: broken
        ? "Windows broken/chipped per survey  |  $1,500/window"
        : "Optional — windows not broken per survey",
    };
  },
  "Security Gate — Repair": (a) => {
    const type = a.text("Security gate type");
    if (!type) return {};
    return {
      note: /none/i.test(type)
        ? "Optional — security gate type: None per survey"
        : `Optional — gate type: ${type} per survey  |  $1,500 repair`,
    };
  },
  "Dumpster Enclosure — Gate Repair": (a) => {
    const c = a.text("Dumpster enclosure condition");
    return { note: c ? `Optional — dumpster enclosure condition: ${c} per survey` : null };
  },
  "Exterior Wall Repair": (a) => {
    const wall = a.num("Overall exterior wall condition");
    const damage = comment(a.text("Overall exterior building damage (metal panels, masonry, etc.)"));
    if (wall === null && !damage) return {};
    const parts = [
      wall !== null ? `overall exterior wall: ${cond(wall)}` : null,
      damage ? `damage: "${damage}"` : null,
    ].filter(Boolean);
    return { note: `Optional — ${parts.join("  |  ")} per survey` };
  },

  // ── EXTERIOR SIGNAGE & LIGHTING ──
  // Formula Reference Guide: $7,500/sign, $15,000 for both (fascia + pylon).
  // Only a sign whose type answer reads "Damaged/Missing Face" (pylon) or
  // "Missing/Damaged Face" (building) prices — an intact sign of any other
  // listed type (Stretched Dollar, Corrugated, Individual Letters, Shared
  // Structure, …) isn't a GC scope item.
  "Building Signs — Fascia + Pylon": (a) => {
    const buildingType = a.text("Existing building sign type");
    const pylonType = a.text("Existing pylon sign type");
    if (buildingType === null && pylonType === null) return {};
    const buildingDamaged = buildingType !== null && /damaged/i.test(buildingType);
    const pylonDamaged = pylonType !== null && /damaged/i.test(pylonType);
    const detail = [
      buildingType ? `building: "${buildingType}"` : null,
      pylonType ? `pylon: "${pylonType}"` : null,
    ]
      .filter(Boolean)
      .join(", ");
    if (!buildingDamaged && !pylonDamaged) {
      return { note: `No damaged/missing sign face per survey  |  ${detail}` };
    }
    const damaged = [buildingDamaged ? "building" : null, pylonDamaged ? "pylon" : null].filter(Boolean);
    const cost = damaged.length * 7500;
    return {
      note: `$${fmt(cost)}${damaged.length > 1 ? " (both signs)" : ""}  |  ${damaged.join(" + ")} sign${damaged.length > 1 ? "s" : ""} damaged/missing face per survey  |  ${detail}`,
      cost,
    };
  },
  "Exterior Lighting (LED retrofit)": (a) => {
    const n = a.num("Number of exterior bulbs needing replacement (LED retrofit)");
    if (n === null) return {};
    return {
      note: `Optional — ${n} bulbs needing replacement per survey${n > 0 ? `  |  ${n} × $50/bulb` : ""}`,
      cost: n > 0 ? n * 50 : null,
    };
  },
  "Pylon Sign Refurbishment": (a) => {
    const on = a.yes("Does the pylon road sign turn on?");
    const type = a.text("Existing pylon sign type");
    if (on === null && !type) return {};
    return {
      note: `Optional — pylon sign ${on === null ? "" : on ? "turns on" : "does NOT turn on"}${type ? `, type: ${type}` : ""} per survey`,
    };
  },
  "Light Pole — Repair / Replace": (a) => {
    const n = a.num("Number of damaged or missing light poles");
    if (n === null) return {};
    return {
      note: `Optional — ${n} damaged poles per survey${n > 0 ? `  |  ${n} × $2,000/pole` : ""}`,
      cost: n > 0 ? n * 2000 : null,
    };
  },
  "Wall Pack — Repair / Replace": (a) => {
    const n = a.num("Number of damaged or missing wall packs");
    if (n === null) return {};
    return {
      note: `Optional — ${n} damaged wall packs per survey${n > 0 ? `  |  ${n} × $1,000/each` : ""}`,
      cost: n > 0 ? n * 1000 : null,
    };
  },
  "Under-Canopy Light Replacement": (a) => {
    const n = a.num("Number of rusted, damaged, or missing under-canopy lights");
    if (n === null) return {};
    return { note: `Optional — ${n} rusted/damaged under-canopy lights per survey` };
  },
  "Sign Illumination Repair": (a) => {
    const building = a.yes("Does the building sign turn on?");
    const pylon = a.yes("Does the pylon road sign turn on?");
    if (building === null && pylon === null) return {};
    if (building !== false && pylon !== false) {
      return { note: "Optional — building & pylon signs both turn on per survey" };
    }
    const failing = [building === false ? "building" : null, pylon === false ? "pylon" : null]
      .filter(Boolean)
      .join(" + ");
    return { note: `${failing} sign not illuminating per survey` };
  },

  // ── LANDSCAPING ──
  "Landscape Refresh": (a) => {
    const feedback = a.text(
      "Overall feedback on landscaping condition (retention ponds, lawn, bushes, shrubs)"
    );
    const impeded = [
      "Left side of property — visibility to pylon or building impeded by vegetation?",
      "Front of building — visibility to front doors impeded by vegetation?",
      "Right side of property — visibility to pylon or building impeded by vegetation?",
      "When exiting the lot, is visibility impeded by vegetation?",
    ].some((q) => a.yes(q) === true);
    if (feedback === null && !impeded) return {};
    const issue = comment(feedback);
    if (!issue && !impeded) {
      return { note: "Optional — no landscaping issues, no vegetation impedance per survey" };
    }
    const parts = [issue ? `"${issue}"` : null, impeded ? "vegetation impeding visibility" : null].filter(Boolean);
    return { note: `${parts.join("  |  ")} per survey  |  $500–$2,000 allowance` };
  },
  "Tree Trimming / Vegetation Clearing": (a) => {
    const trees = a.yes("Trees overhanging building or vegetation touching building?");
    if (trees === null) return {};
    return {
      note: trees
        ? "Trees overhanging / touching building per survey"
        : "Optional — no trees overhanging per survey",
    };
  },
  "Drainage / Grading Work": (a) => {
    const raw = a.text("Drainage or grading needs around the property?");
    if (!raw) return {};
    const c = comment(raw);
    return { note: c ? `"${c}" per survey` : "Optional — no drainage/grading needs per survey" };
  },
  "Fence / Retaining Wall Repair": (a) => {
    const raw = a.text("Damage to fencing or retaining walls surrounding the property?");
    if (!raw) return {};
    const c = comment(raw);
    return { note: c ? `"${c}" per survey` : "Optional — no fence/retaining wall damage per survey" };
  },

  // ── ADA ──
  "ADA Receiving Ramp (R&R)": (a) => {
    const compliant = a.yes("ADA ramp at rear / receiving entry compliant?");
    if (compliant === null) return {};
    return {
      note: compliant
        ? "Optional — ramp compliant per survey"
        : "Ramp non-compliant per survey  |  $3,500–$5,000/ramp",
    };
  },
  "ADA Parking Spaces (Marking & Signage)": (a) => {
    const marked = a.yes("ADA parking spaces properly marked?");
    if (marked === null) return {};
    return {
      note: marked
        ? "Optional — ADA spaces properly marked per survey"
        : "ADA spaces not properly marked per survey",
    };
  },
  "ADA Bollards (handicap signage)": (a) => {
    const present = a.yes("ADA bollards present at handicap signage?");
    if (present === null) return {};
    return {
      note: present
        ? "Optional — ADA bollards present per survey"
        : "ADA bollards missing per survey  |  $1,500/ea",
    };
  },
  "Interior ADA Concerns — Misc.": (a) => {
    const raw = a.text("Interior ADA concerns observed?");
    if (!raw) return {};
    const c = comment(raw);
    return { note: c ? `"${c}" per survey` : "Optional — no interior ADA concerns per survey" };
  },

  // ── MISCELLANEOUS ──
  "Architectural Fees / Permits": () => ({ cost: 5000 }),
  "Miscellaneous Allowance": () => ({ cost: 2500 }),
};

const normalizeLabel = (label: string) => label.replace(/\s+/g, " ").trim();

/**
 * Evaluates a template GC-cost formula against the Survey Form cells the
 * export stamped, independent of question titles. Handles the template's two
 * shapes: `'Survey Form'!Dq*IF('Survey Form'!Da="Replace",3500,IF(…,350,0))`
 * and `'Survey Form'!Dq*<rate>`. Returns null when the formula isn't
 * survey-driven, else the computed cost (0 = the row is inert).
 */
function evalSurveyFormula(
  formula: string,
  cellValue: (ref: string) => string | number | undefined
): { cost: number; qty: number; action: string | null } | null {
  const refs = Array.from(formula.matchAll(/'Survey Form'!\$?([A-Z]+)\$?(\d+)/gi)).map(
    (m) => `${m[1].toUpperCase()}${m[2]}`
  );
  if (refs.length === 0) return null;

  const rawQty = cellValue(refs[0]);
  const qty = typeof rawQty === "number" ? rawQty : Number(String(rawQty ?? "").replace(/,/g, ""));
  const action = refs[1] ? String(cellValue(refs[1]) ?? "") : "";
  if (!Number.isFinite(qty) || qty <= 0) return { cost: 0, qty: 0, action: action || null };

  if (/\bIF\s*\(/i.test(formula) && refs[1]) {
    const rate = /replace/i.test(action) ? 3500 : /tune-?up/i.test(action) ? 350 : 0;
    return { cost: Math.floor(qty * rate), qty, action: action || null };
  }
  const rate = formula.match(/\*\s*(\d+(?:\.\d+)?)/);
  return rate
    ? { cost: Math.floor(qty * Number(rate[1])), qty, action: null }
    : { cost: qty, qty, action: null };
}

export interface GcFormulaContext {
  /** GC Evaluation column-D formulas by row (readSheetColumnFormulas). */
  formulasByRow: Map<number, string>;
  /** Value the export is stamping into a Survey Form cell, by A1 ref. */
  surveyCellValue: (ref: string) => string | number | undefined;
}

export interface GcEvaluationResult {
  writes: CellWrite[];
  /** Rows with a cost (typed or live formula) — the template's "active" look. */
  activeRows: number[];
  /** Rows left without any estimate — should read as disabled (grey). */
  disabledRows: number[];
}

/** Pure core: rules × row labels × answers → cell writes (testable without zip). */
export function buildGcEvaluationWrites(
  labelsByRow: Map<number, string>,
  answerMap: Map<string, unknown>,
  formulaCtx?: GcFormulaContext
): GcEvaluationResult {
  const answers = new SurveyAnswers(answerMap);
  const rowByLabel = new Map<string, number>();
  for (const [row, label] of labelsByRow) {
    const key = normalizeLabel(label);
    if (!rowByLabel.has(key)) rowByLabel.set(key, row);
  }

  const writes: CellWrite[] = [];
  const notedRows = new Set<number>();
  const active = new Set<number>();
  const disabled = new Set<number>();
  for (const [label, rule] of Object.entries(RULES)) {
    const row = rowByLabel.get(normalizeLabel(label));
    if (row === undefined) continue;
    // Formula-backed rows are settled by the formula pass below.
    const hasFormula = formulaCtx?.formulasByRow.has(row) ?? false;
    const { note, cost } = rule(answers);
    if (note) {
      writes.push({ sheet: GC_EVALUATION_SHEET, cell: `C${row}`, value: note });
      notedRows.add(row);
    }
    if (cost !== null && cost !== undefined) {
      // A live template formula computes this same figure itself — leave it
      // in place (the formula pass settles the row); only stamp the number
      // where the template ships without one (the "BLANK formulas" revision).
      if (!hasFormula) {
        writes.push({ sheet: GC_EVALUATION_SHEET, cell: `D${row}`, value: cost });
        active.add(row);
      }
    } else if (!hasFormula) {
      // No computed cost: clear the template's static 0 so the row reads as
      // disabled instead of $0.
      writes.push({ sheet: GC_EVALUATION_SHEET, cell: `D${row}`, value: null });
      disabled.add(row);
    }
  }

  // Formula pass: rows whose cost is a live 'Survey Form' formula. Evaluating
  // against the stamped cells needs no question-title matching — an inert
  // formula (no unit, zero deficit) is force-cleared so the row reads as
  // disabled, and an active one gets its math spelled out in the basis when
  // no rule already annotated it.
  if (formulaCtx) {
    const labelOf = (row: number) => normalizeLabel(labelsByRow.get(row) ?? "");
    for (const [row, formula] of formulaCtx.formulasByRow) {
      const result = evalSurveyFormula(formula, formulaCtx.surveyCellValue);
      if (!result) continue;
      if (result.cost <= 0) {
        disabled.add(row);
        active.delete(row);
        writes.push({ sheet: GC_EVALUATION_SHEET, cell: `D${row}`, value: null, force: true });
        if (!notedRows.has(row) && /^HVAC — Unit (\d)/.test(labelOf(row))) {
          const unit = labelOf(row).match(/^HVAC — Unit (\d)/)![1];
          // Say exactly which input is missing — a unit with partial data
          // (age/serial but no tonnage) is a survey gap, not an absent unit.
          const hasOtherData =
            ["Age (years)", "Age", "Serial Number", "Model Number"].some(
              (suffix) => answers.text(`Unit ${unit} — ${suffix}`) !== null
            );
          let note: string;
          if (result.qty > 0) {
            note = `Unit ${unit} (${result.qty}t) — no action recorded per survey`;
          } else if (result.action) {
            note = `Unit ${unit} — ${result.action} recorded, tonnage MISSING per survey (cost pending)`;
          } else if (hasOtherData) {
            note = `Unit ${unit} — incomplete survey data (no tonnage/action); cost pending`;
          } else {
            note = `No Unit ${unit} per survey`;
          }
          writes.push({ sheet: GC_EVALUATION_SHEET, cell: `C${row}`, value: note });
        }
      } else {
        active.add(row);
        disabled.delete(row);
        if (!notedRows.has(row) && result.action) {
          const rate = /replace/i.test(result.action) ? 3500 : 350;
          writes.push({
            sheet: GC_EVALUATION_SHEET,
            cell: `C${row}`,
            value: `${result.qty}t × $${fmt(rate)}/ton (${result.action}) = $${fmt(result.cost)}  |  per Survey Form`,
          });
        }
      }
    }
  }

  return { writes, activeRows: [...active], disabledRows: [...disabled] };
}

/** Columns whose look flips between the template's active and grey row styles. */
const GC_RESTYLE_COLUMNS = ["B", "C", "D", "E", "F", "G", "H", "I", "J"];

export type GcRestyleContext = Map<string, string>;

/**
 * Per-column style indexes of the template's grey "Optional" reference row.
 *
 * The sheet's conditional formatting paints the active looks live (cream when
 * the row has a price, green when confirmed) but its grey rule changes the
 * font only — so a cell whose BASE style bakes in the cream fill (the unit
 * rows' Estimated Total column) never greys out when a price is removed.
 * Normalizing every rule row's base to the grey look makes the shading fully
 * dynamic: type a price and the row turns cream, delete it and the whole row
 * — J included — reverts to the same grey.
 */
export async function buildGcRestyleContext(
  templateBuf: ArrayBuffer,
  labelsByRow: Map<number, string>
): Promise<GcRestyleContext | null> {
  let greyRow: number | undefined;
  for (const [row, label] of labelsByRow) {
    if (normalizeLabel(label) === normalizeLabel("HVAC — Refrigerant / Coil Service")) {
      greyRow = row;
      break;
    }
  }
  if (greyRow === undefined) return null;
  const grey = await readRowStyles(templateBuf, GC_EVALUATION_SHEET, greyRow);
  return grey.size > 0 ? grey : null;
}

/** Grey-base restyles for every rule row (see buildGcRestyleContext). */
export function buildGcRowRestyles(
  result: GcEvaluationResult,
  ctx: GcRestyleContext | null
): CellRestyle[] {
  if (!ctx) return [];
  const restyles: CellRestyle[] = [];
  for (const row of [...result.disabledRows, ...result.activeRows]) {
    for (const col of GC_RESTYLE_COLUMNS) {
      const styleIndex = ctx.get(col);
      if (styleIndex) restyles.push({ sheet: GC_EVALUATION_SHEET, cell: `${col}${row}`, styleIndex });
    }
  }
  return restyles;
}

/**
 * GC Evaluation writes + row restyles for a template workbook. Empty when the
 * workbook has no "GC Evaluation" sheet (non-DG templates). `surveyWrites`
 * are the export's pending answer writes, used to evaluate cost formulas.
 */
export async function collectGcEvaluationWrites(
  templateBuf: ArrayBuffer,
  answerMap: Map<string, unknown>,
  surveyWrites?: CellWrite[],
  defaultSheet?: string | null
): Promise<{ writes: CellWrite[]; restyles: CellRestyle[] }> {
  const labels = await readSheetColumnText(templateBuf, GC_EVALUATION_SHEET, "B");
  if (labels.size === 0) return { writes: [], restyles: [] };
  const formulasByRow = await readSheetColumnFormulas(templateBuf, GC_EVALUATION_SHEET, "D");
  const result = buildGcEvaluationWrites(labels, answerMap, {
    formulasByRow,
    surveyCellValue: buildSurveyCellLookup(surveyWrites ?? [], defaultSheet),
  });
  const ctx = await buildGcRestyleContext(templateBuf, labels);
  return { writes: result.writes, restyles: buildGcRowRestyles(result, ctx) };
}

const SURVEY_FORM_SHEET = "Survey Form";

/** ref → value lookup over the writes that land on the Survey Form sheet. */
export function buildSurveyCellLookup(
  surveyWrites: CellWrite[],
  defaultSheet?: string | null
): (ref: string) => string | number | undefined {
  const byRef = new Map<string, string | number>();
  for (const w of surveyWrites) {
    if (w.value === null) continue;
    const sheet = w.sheet ?? defaultSheet ?? SURVEY_FORM_SHEET;
    if (sheet.trim().toLowerCase() !== SURVEY_FORM_SHEET.toLowerCase()) continue;
    byRef.set(w.cell.toUpperCase(), w.value);
  }
  return (ref) => byRef.get(ref.toUpperCase());
}
